import {
  transformOas3Operation,
  transformOas3Service,
} from '@stoplight/http-spec/oas3'
import { get, isObject, last } from 'lodash'
import { OpenAPIObject } from 'openapi3-ts'
import { Spec } from 'swagger-schema-official'

import {
  Oas3HttpOperationTransformer,
  Oas3HttpServiceTransformer,
} from '@stoplight/http-spec/oas/types'
import { encodePointerFragment, pointerToPath } from '@stoplight/json'
import { NodeType } from '@stoplight/types'

import {
  ISourceNodeMap,
  NodeTypes,
  ServiceChildNode,
  ServiceNode,
} from '../types'

const oas3SourceMap: ISourceNodeMap[] = [
  {
    match: 'paths',
    type: NodeTypes.Paths,
    children: [
      {
        notMatch: '^x-',
        type: NodeTypes.Path,
        children: [
          {
            match: 'get|post|put|delete|options|head|patch|trace',
            type: NodeTypes.Operation,
          },
        ],
      },
    ],
  },
  {
    match: 'components',
    type: NodeTypes.Components,
    children: [
      {
        match: 'schemas',
        type: NodeTypes.Models,
        children: [
          {
            notMatch: '^x-',
            type: NodeTypes.Model,
          },
        ],
      },
    ],
  },
]

const OAS_MODEL_REGEXP = /((definitions|components)\/?(schemas)?)\//

export function transformOasToServiceNode(apiDescriptionDocument: unknown) {
  if (apiDescriptionDocument) {
    return computeServiceNode(
      {
        ...(apiDescriptionDocument as OpenAPIObject),
        jsonSchemaDialect: 'http://json-schema.org/draft-07/schema#',
      },
      oas3SourceMap,
      transformOas3Service,
      transformOas3Operation
    )
  }

  return null
}

function computeServiceNode(
  document: Spec | OpenAPIObject,
  map: ISourceNodeMap[],
  transformService: Oas3HttpServiceTransformer,
  transformOperation: Oas3HttpOperationTransformer
) {
  const serviceDocument = transformService({ document })
  const serviceNode: ServiceNode = {
    type: NodeType.HttpService,
    uri: '/',
    name: serviceDocument.name,
    data: serviceDocument,
    tags: serviceDocument.tags?.map((tag) => tag.name) || [],
    children: computeChildNodes(document, document, map, transformOperation),
  }

  return serviceNode
}

function computeChildNodes(
  document: Spec | OpenAPIObject,
  data: unknown,
  map: ISourceNodeMap[],
  transformer: Oas3HttpOperationTransformer,
  parentUri: string = ''
) {
  const nodes: ServiceChildNode[] = []

  if (!isObject(data)) return nodes

  for (const key of Object.keys(data)) {
    const sanitizedKey = encodePointerFragment(key)
    const match = findMapMatch(sanitizedKey, map)
    if (match) {
      const uri = `${parentUri}/${sanitizedKey}`

      const jsonPath = pointerToPath(`#${uri}`)
      if (match.type === NodeTypes.Operation && jsonPath.length === 3) {
        const path = String(jsonPath[1])
        const method = String(jsonPath[2])
        const operationDocument = transformer({ document, path, method })
        let parsedUri
        const encodedPath = String(encodePointerFragment(path))

        if (operationDocument.iid) {
          parsedUri = `/operations/${operationDocument.iid}`
        } else {
          parsedUri = uri.replace(encodedPath, slugify(path))
        }

        nodes.push({
          type: NodeType.HttpOperation,
          uri: parsedUri,
          data: operationDocument,
          name:
            operationDocument.summary ||
            operationDocument.iid ||
            operationDocument.path,
          tags: operationDocument.tags?.map((tag) => tag.name) || [],
        })
      } else if (match.type === NodeTypes.Model) {
        const schemaDocument = get(document, jsonPath)
        const parsedUri = uri.replace(OAS_MODEL_REGEXP, 'schemas/')

        nodes.push({
          type: NodeType.Model,
          uri: parsedUri,
          data: schemaDocument,
          name: schemaDocument.title || last(uri.split('/')) || '',
          tags: schemaDocument['x-tags'] || [],
        })
      }

      if (match.children) {
        nodes.push(
          ...computeChildNodes(
            document,
            // @ts-ignore
            data[key],
            match.children,
            transformer,
            uri
          )
        )
      }
    }
  }

  return nodes
}

function slugify(name: string) {
  return name
    .replace(/\/|{|}|\s/g, '-')
    .replace(/-{2,}/, '-')
    .replace(/^-/, '')
    .replace(/-$/, '')
}

function findMapMatch(
  key: string | number,
  map: ISourceNodeMap[]
): ISourceNodeMap | void {
  if (typeof key === 'number') return

  for (const entry of map) {
    if (
      !!entry.match?.match(key) ||
      (entry.notMatch !== void 0 && !entry.notMatch.match(key))
    ) {
      return entry
    }
  }
}
